import React, { FC, useRef, useState, useEffect } from "react";
import "./VideoShow.scss";

function formatDuration(seconds: number): string {
  if (isNaN(seconds)) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export interface VideoShowProps {
  /** 视频地址，不传则显示占位 */
  videoSrc?: string | null;
  poster?: string;
  /** 标题，如「第一集」 */
  title?: string;
  className?: string;
  /** 覆盖在视频上的内容（如自动字幕的 Rnd 选区） */
  children?: React.ReactNode;
  /** 绑定到 .auto-video-inner 的 ref（如自动识别字幕弹窗需要用于计算 ROI） */
  innerRef?: React.RefObject<HTMLDivElement | null>;
}

export const VideoShow: FC<VideoShowProps> = ({
  videoSrc,
  poster,
  title,
  className = "",
  children,
  innerRef,
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const progressFillRef = useRef<HTMLDivElement | null>(null);
  const progressThumbRef = useRef<HTMLDivElement | null>(null);
  const progressContainerRef = useRef<HTMLDivElement | null>(null);
  const currentTimeTextRef = useRef<HTMLSpanElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);

  const syncProgressUI = (time: number, totalDuration?: number) => {
    const safeDuration = totalDuration ?? videoRef.current?.duration ?? duration ?? 0;
    const progress = safeDuration > 0 ? (time / safeDuration) * 100 : 0;
    const clampedProgress = Math.min(100, Math.max(0, progress));

    if (progressThumbRef.current) {
      progressThumbRef.current.style.left = `${clampedProgress}%`;
    }
    if (progressFillRef.current) {
      progressFillRef.current.style.width = `${clampedProgress}%`;
    }
    if (currentTimeTextRef.current) {
      currentTimeTextRef.current.innerText = formatDuration(time);
    }
  };

  const seekToClientX = (clientX: number) => {
    if (!videoRef.current || !duration || !progressContainerRef.current) return;

    const rect = progressContainerRef.current.getBoundingClientRect();
    const pos = (clientX - rect.left) / rect.width;
    const clamped = Math.max(0, Math.min(1, pos));
    const newTime = clamped * duration;

    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    syncProgressUI(newTime, duration);
  };

  useEffect(() => {
    const updateProgress = () => {
      const el = videoRef.current;
      if (!el || !playing) return;
      const current = el.currentTime || 0;
      const dur = el.duration || duration || 0;
      syncProgressUI(current, dur);
      setCurrentTime(current);
      animationRef.current = requestAnimationFrame(updateProgress);
    };
    if (playing) {
      animationRef.current = requestAnimationFrame(updateProgress);
    }
    return () => {
      if (animationRef.current != null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [playing, duration]);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setPlaying(false);
    isDraggingRef.current = false;
    syncProgressUI(0, 0);
  }, [videoSrc]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (
        !isDraggingRef.current ||
        !videoRef.current ||
        !duration ||
        !progressContainerRef.current
      )
        return;
      seekToClientX(e.clientX);
    };
    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [duration]);

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    seekToClientX(e.clientX);
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDraggingRef.current = true;
    e.preventDefault();
    seekToClientX(e.clientX);
  };

  const handlePlayPause = () => {
    const el = videoRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const hasVideo = !!videoSrc;

  return (
    <div className={`video-show ${className}`.trim()}>
      {title && <div className="video-show-title">{title}</div>}
      <div className="auto-video-box">
        <div className="auto-video-inner" ref={innerRef}>
          {!hasVideo ? (
            <div className="video-preview-placeholder">暂无可预览视频</div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={videoSrc}
                className="auto-video-element"
                poster={poster}
                playsInline
                controls={false}
                onContextMenu={(e) => e.preventDefault()}
                onLoadedMetadata={(e) => {
                  const el = e.currentTarget;
                  const nextDuration = el.duration || 0;
                  const nextCurrentTime = el.currentTime || 0;
                  setDuration(nextDuration);
                  setCurrentTime(nextCurrentTime);
                  syncProgressUI(nextCurrentTime, nextDuration);
                }}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onSeeked={(e) => {
                  const el = e.currentTarget;
                  const nextTime = el.currentTime || 0;
                  setCurrentTime(nextTime);
                  syncProgressUI(nextTime, el.duration || duration || 0);
                }}
                onEnded={() => setPlaying(false)}
              />
              {children}
            </>
          )}
        </div>
      </div>
      {hasVideo && (
        <div className="video-timebar">
          <div
            className="progress-container"
            ref={progressContainerRef}
            onClick={handleSeek}
            onMouseDown={handleProgressMouseDown}
          >
            <div className="progress-rail" />
            <div
              ref={progressFillRef}
              className="progress-fill"
              style={{
                width:
                  duration > 0
                    ? `${Math.min(100, Math.max(0, (currentTime / duration) * 100))}%`
                    : "0%",
              }}
            />
            <div
              ref={progressThumbRef}
              className="video-progress-thumb"
              style={{
                left:
                  duration > 0
                    ? `${Math.min(100, Math.max(0, (currentTime / duration) * 100))}%`
                    : "0%",
              }}
            />
          </div>
          <div className="video-controls-row">
            <span ref={currentTimeTextRef} className="time-text time-left">
              {formatDuration(currentTime)}
            </span>
            <div className="play-button" onClick={handlePlayPause}>
              <div className={`play-triangle ${playing ? "paused" : ""}`} />
            </div>
            <span className="time-text time-right">
              {duration > 0 ? formatDuration(duration) : "00:00"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
